import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { Amplify } from 'aws-amplify';

import { getConfig } from './config';
import { AuthProvider, RequireAuth } from './auth/Auth';
import { ApiProvider } from './api/Api';
import ErrorBoundary from './ErrorBoundary';
import SignUpPage from './auth/SignUpPage';
import SignInPage from './auth/SignInPage';
import HomePage from './home/HomePage';
import VerifyEmailPage from './auth/VerifyEmailPage';
import ForgotPasswordPage from './auth/ForgotPasswordPage';
import Navbar from './navbar/Navbar';
import EventsPage from './events/EventsPage';
import MusicPage from './music/MusicPage';
import NotFoundPage from './NotFoundPage';
import ThemeProvider from './ThemeProvider';
import VideosPage from './videos/VideosPage';
import AdminPage from './admin/AdminPage';
import ProfilePage from './profile/ProfilePage';
import ProfileEditorPage from './profile/editor/ProfileEditorPage';
import { CacheProvider } from './api/cache/Cache';
import TheSourcePage from './theSource/TheSourcePage';
import InterstellarPage from './interstellar/InterstellarPage';
import ForumDetailPage from './interstellar/ForumDetailPage';
import TopicDetailPage from './interstellar/TopicDetailPage';
import HelpPage from './help/HelpPage';
import BehindTheScenesPage from './bts/BehindTheScenesPage';
import AppDataLoader from './AppDataLoader';

const config = getConfig();
Amplify.configure({
    Auth: {
        region: config.auth.region,
        userPoolId: config.auth.userPoolId,
        userPoolWebClientId: config.auth.userPoolWebClientId,
        oauth: {
            domain: config.auth.oauth.domain,
            scope: config.auth.oauth.scope,
            redirectSignIn: config.auth.oauth.redirectSignIn,
            redirectSignOut: config.auth.oauth.redirectSignOut,
            responseType: config.auth.oauth.responseType,
        },
    },
});

const COMMUNITY_BG = '#060f1e';

function CommunityBodyBackground() {
    const location = useLocation();
    const isCommunityPage =
        location.pathname.startsWith('/ess') ||
        location.pathname.startsWith('/ATWW');

    useEffect(() => {
        document.body.style.transition = 'background-color 0.4s ease';
        document.body.style.backgroundColor = isCommunityPage ? COMMUNITY_BG : '';
    }, [isCommunityPage]);

    return null;
}

function App() {
    return (
        <AuthProvider>
            <ThemeProvider>
                <ApiProvider>
                    <CacheProvider>
                        <AppDataLoader />
                        <BrowserRouter>
                            <CommunityBodyBackground />
                            <Navbar />
                            <ErrorBoundary>
                                <Router />
                            </ErrorBoundary>
                        </BrowserRouter>
                    </CacheProvider>
                </ApiProvider>
            </ThemeProvider>
        </AuthProvider>
    );
}

function Router() {
    const location = useLocation();
    return (
        <AnimatePresence mode='wait'>
            <motion.div
                key={location.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeInOut' }}
                style={{ width: '100%' }}
            >
        <Routes location={location}>
            <Route path='/'>
                <Route index element={<HomePage />} />
                <Route path='signin' element={<SignInPage />} />
                <Route path='signup' element={<SignUpPage />} />
                <Route path='verify-email' element={<VerifyEmailPage />} />
                <Route path='forgot-password' element={<ForgotPasswordPage />} />
                <Route path='the-source' element={<TheSourcePage />} />
                <Route path='ATWW' element={<BehindTheScenesPage />} />
                <Route path='events' element={<EventsPage />} />
                <Route path='music' element={<MusicPage />} />
                <Route path='videos' element={<VideosPage />} />
                <Route path='ess'>
                    <Route index element={<InterstellarPage />} />
                    <Route path='edit' element={<ProfileEditorPage />} />
                    <Route path=':forumNameAndSk' element={<ForumDetailPage />} />
                    <Route
                        path=':forumNameAndSk/topic/:topicNameAndSk'
                        element={<TopicDetailPage />}
                    />
                </Route>
                <Route path='help' element={<HelpPage />} />
                <Route element={<RequireAuth />}>
                    <Route path='profile'>
                        <Route index element={<ProfilePage />} />
                        <Route path='edit' element={<ProfileEditorPage />} />
                    </Route>
                    <Route path='admin' element={<AdminPage />} />
                </Route>
                <Route path='profile/:username' element={<ProfilePage />} />
                <Route path='*' element={<NotFoundPage />} />
            </Route>
        </Routes>
            </motion.div>
        </AnimatePresence>
    );
}
export default App;
